import { FC, useMemo } from "react";
import { Chip } from "../Chip";
import { DateRangePicker, type DateRangeValue } from "../DateRangePicker";
import styles from "./FilteringOptionsPanel.module.scss";

export type DateFilterChipsProps = {
    /** Unix seconds (start of range / preset). */
    start: number | null | undefined;
    /** Unix seconds (end of range). Null for open-ended presets. */
    end: number | null | undefined;
    onPreset: (start?: number | null, end?: number | null) => void;
    onRangeChange: (range: { from: Date; to: Date } | null) => void;
};

/**
 * Date filter chips: All / Today / 24 Hours / Range.
 * Presentational only — callers own filter state.
 */
export const DateFilterChips: FC<DateFilterChipsProps> = ({
    start,
    end,
    onPreset,
    onRangeChange,
}) => {
    const isAll = start == null && end == null;
    const now = new Date();
    const todayStart = Math.floor(
        new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() /
            1000,
    );
    const twentyFourHoursAgo = Math.floor(
        (Date.now() - 24 * 60 * 60 * 1000) / 1000,
    );

    const isTodayPreset = start === todayStart && end == null;
    const is24hPreset =
        end == null &&
        start != null &&
        Math.abs(start - twentyFourHoursAgo) < 120;
    const isCustomRange = start != null && end != null;

    const rangeValue = useMemo((): DateRangeValue | null => {
        if (!isCustomRange) return null;
        return {
            from: new Date((start as number) * 1000),
            to: new Date((end as number) * 1000),
        };
    }, [isCustomRange, start, end]);

    return (
        <div className={`${styles.grid} ${styles.dateGrid}`}>
            <Chip
                className={styles.chip}
                label="All"
                onClick={() => onPreset(null, null)}
                selected={isAll}
                type="button"
            />
            <Chip
                className={styles.chip}
                label="Today"
                onClick={() => onPreset(todayStart, null)}
                selected={isTodayPreset}
                type="button"
            />
            <Chip
                className={styles.chip}
                label="24 Hours"
                onClick={() => onPreset(twentyFourHoursAgo, null)}
                selected={is24hPreset}
                type="button"
            />
            <div className={styles.rangeWrapper}>
                <DateRangePicker
                    className={styles.rangePicker}
                    dateFormat="MMM do yy"
                    fullWidth
                    label="Range"
                    numberOfMonths={2}
                    onChange={range => {
                        if (!range?.from || !range?.to) {
                            onRangeChange(null);
                            return;
                        }
                        onRangeChange({ from: range.from, to: range.to });
                    }}
                    trigger="button"
                    value={rangeValue}
                />
            </div>
        </div>
    );
};

DateFilterChips.displayName = "DateFilterChips";
